/**
 * Shared analytics metric helpers for admin overlays and editor modules.
 */

export function formatMetric(value) {
    return new Intl.NumberFormat('en-US').format(Number(value) || 0);
}

export function formatRatio(value) {
    const n = Number(value) || 0;
    return n.toFixed(2);
}

export function formatDuration(totalSeconds) {
    const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remSeconds = seconds % 60;
    if (minutes < 60) return remSeconds ? `${minutes}m ${remSeconds}s` : `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`;
}

export function pagesPerVisitor(pageviews, visitors, visits = 0, bounces = 0, excludeBounced = false) {
    const pv = Number(pageviews) || 0;
    const v = Number(visitors) || 0;
    const vs = Number(visits) || 0;
    const b = Number(bounces) || 0;
    const denominator = excludeBounced ? (vs - b) : v;
    if (denominator <= 0) return 0;
    return pv / denominator;
}

export function avgTimePerVisit(totaltime, visits) {
    const total = Number(totaltime) || 0;
    const v = Number(visits) || 0;
    if (v <= 0) return 0;
    return total / v;
}

export function calculateTrend(current, previous) {
    const currentValue = Number(current) || 0;
    const previousValue = Number(previous) || 0;
    if (previousValue <= 0) {
        if (currentValue <= 0) return { pct: 0, direction: 'flat' };
        return { pct: 100, direction: 'up' };
    }
    const pct = ((currentValue - previousValue) / previousValue) * 100;
    if (pct > 0) return { pct, direction: 'up' };
    if (pct < 0) return { pct, direction: 'down' };
    return { pct: 0, direction: 'flat' };
}

export function trendClasses(direction) {
    if (direction === 'up') return 'text-green-700 bg-green-50';
    if (direction === 'down') return 'text-red-700 bg-red-50';
    return 'text-gray-700 bg-gray-100';
}

export function trendText(trend) {
    const rounded = Math.round(Math.abs(trend?.pct || 0));
    if (!trend || trend.direction === 'flat') return '0%';
    return `${trend.direction === 'up' ? '+' : '-'}${rounded}%`;
}

export function emptyImageAnalytics() {
    return {
        visitors: 0,
        visits: 0,
        pageviews: 0,
        avgTimeSeconds: 0,
        visitorsTrend: { pct: 0, direction: 'flat' },
        visitsTrend: { pct: 0, direction: 'flat' },
        pageviewsTrend: { pct: 0, direction: 'flat' },
        avgTimeTrend: { pct: 0, direction: 'flat' },
    };
}

/** True when a media or page metrics object has any recorded traffic. */
export function hasAnalyticsData(analytics) {
    if (!analytics) return false;
    return ['visitors', 'visits', 'pageviews', 'avgTimeSeconds']
        .some((key) => Number(analytics[key]) > 0);
}

/**
 * Normalize a path metrics row + previous row into the shape used by overlays
 * and ImageManager tiles.
 */
export function buildMetric(row, prevRow) {
    return {
        path: row?.path,
        label: row?.label || row?.originalName || row?.description || row?.path,
        pageName: row?.pageName,
        thumbnailUrl: row?.thumbnailUrl,
        kind: row?.kind,
        matched: row?.matched !== false,
        visitors: Number(row?.visitors) || 0,
        visits: Number(row?.visits) || 0,
        pageviews: Number(row?.pageviews) || 0,
        avgTimeSeconds: avgTimePerVisit(row?.totaltime, row?.visits),
        previousVisitors: Number(prevRow?.visitors) || 0,
        previousVisits: Number(prevRow?.visits) || 0,
        previousPageviews: Number(prevRow?.pageviews) || 0,
        previousAvgTimeSeconds: avgTimePerVisit(prevRow?.totaltime, prevRow?.visits),
        visitorsTrend: calculateTrend(row?.visitors, prevRow?.visitors),
        visitsTrend: calculateTrend(row?.visits, prevRow?.visits),
        pageviewsTrend: calculateTrend(row?.pageviews, prevRow?.pageviews),
        avgTimeTrend: calculateTrend(
            avgTimePerVisit(row?.totaltime, row?.visits),
            avgTimePerVisit(prevRow?.totaltime, prevRow?.visits),
        ),
    };
}
